// Which of the carrier's E911 sites this address is.
//
// The most consequential thing on the location page, and until now it had no control at
// all: an address either got a site when it was created, or never. That gap is what let a
// live number keep dispatching 911 to a previous owner's address — Verso knew the service
// address, Commio did not, and nothing joined them or complained.
//
// It is also the state the Commio importer routinely produces: it creates its own Verso
// row per carrier site, so an address entered by hand sits beside an imported twin, one
// holding the customer and the other holding the registration.
//
// LINKING ONLY. Nothing here creates or edits a carrier record; it decides which existing
// one this address means. The type has to agree (Commio rejects a business caller-ID over a
// residential site), and a site belongs to one address — moving it is offered, but only
// after saying what the other address loses.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

export default function E911SiteCard({ location, canEdit }) {
  const qc = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [choice, setChoice] = useState('');
  const [query, setQuery] = useState('');
  const [takeOverFrom, setTakeOverFrom] = useState(null);

  const linkedId = location.commioLocationId ?? location.commio_location_id ?? null;
  const erlId = location.erlId ?? location.erl_id ?? null;

  const sites = useQuery({
    queryKey: ['commio', 'sites'],
    queryFn: () => api.get('/settings/commio/locations'),
    enabled: picking,
  });

  // Register THIS address with the carrier — create the site, or update the one already
  // linked. Kevin, 2026-08-10: "E911 locations should be able to be pushed to Commio. No
  // import required." Updates rather than creates when linked, because a second site for
  // one address is an E911 duplicate and those decide where an ambulance goes.
  const push = useMutation({
    mutationFn: () => api.post(`/locations/${location.id}/e911/push`, {}),
    // CLOSE THE PICKER. A registration that worked used to leave the whole choose-a-site form
    // open behind its own success line, with `Link this site` dead (nothing is selected — there
    // is nothing left to select) and an `Unlink` button newly appeared beside it. Every
    // statement on screen was true and the thing read as a failure. Kevin, 2026-08-18: "from an
    // Operators perspective it still looks like it failed."
    //
    // The outcome is rendered in the settled view instead, so closing does not throw away the
    // one line that says what happened — including when the read-back could NOT confirm it.
    onSuccess: () => {
      setPicking(false);
      setChoice('');
      setQuery('');
      qc.invalidateQueries({ queryKey: ['location', String(location.id)] });
      qc.invalidateQueries({ queryKey: ['commio-locations'] });
    },
  });

  const link = useMutation({
    mutationFn: (opts = {}) => api.put(`/locations/${location.id}/e911-site`, {
      commio_location_id: opts.unlink ? null : Number(choice),
      ...(opts.takeOver ? { take_over: true } : {}),
    }),
    onSuccess: () => {
      setPicking(false);
      setTakeOverFrom(null);
      setChoice('');
      qc.invalidateQueries({ queryKey: ['location', String(location.id)] });
      qc.invalidateQueries({ queryKey: ['locations'] });
      qc.invalidateQueries({ queryKey: ['commio', 'sites'] });
    },
    onError: (err) => {
      // The one error worth turning into a choice rather than a message.
      if (err?.body?.code === 'site_linked_elsewhere') setTakeOverFrom(err.body.linked_to || {});
    },
  });

  const all = sites.data?.sites || [];
  // Same type only. Offering the rest and letting the server refuse teaches the operator
  // nothing except that the form lies.
  const usable = all.filter((s) => !location.type || s.type === location.type);
  // Match on anything an operator would type: street, city, alias, or the site id.
  const matches = (() => {
    const q = query.trim().toLowerCase();
    if (!q) return usable;
    return usable.filter((s2) => [s2.address, s2.city, s2.alias, String(s2.commio_location_id)]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  })();

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">911 dispatch registration</h2>
          <p className="mt-1 text-sm text-gray-600">
            The carrier record that decides where responders are sent for numbers at this
            address.
            {/* The warning is the whole point of the sentence when it is missing, and noise
                once it is there — read next to "Registered as site #21110" it says the
                opposite of the truth. */}
            {linkedId ? ' Lines here can be built.' : ' Lines here cannot be built until it is set.'}
          </p>
        </div>
        {canEdit && !picking && (
          <button
            type="button"
            onClick={() => { link.reset(); push.reset(); setPicking(true); setChoice(linkedId ? String(linkedId) : ''); }}
            className="flex-shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            {linkedId ? 'Change' : 'Link a site'}
          </button>
        )}
      </div>

      {!picking && (
        <div className="mt-4">
          {linkedId ? (
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Carrier site</dt>
                <dd className="mt-0.5 font-mono text-sm text-gray-900">{linkedId}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">ERL</dt>
                <dd className="mt-0.5 font-mono text-xs text-gray-700">{erlId || <span className="text-gray-300">—</span>}</dd>
              </div>
            </dl>
          ) : (
            // Amber, not grey. An address with no dispatch registration is the one gap in
            // this product that can hurt somebody who is not a customer.
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Not registered with the carrier. A 911 call from a number at this address would
              be dispatched to whatever address the carrier last had for that number —
              possibly a previous owner’s.
            </p>
          )}

          {/* What the register button just did, kept after the picker closes. Green only when
              the carrier confirmed it on a read-back: "we wrote it" is not "it is there", and
              911 is the last place to blur the two. */}
          {push.isSuccess && (
            <p className={`mt-3 rounded-md border px-3 py-2 text-sm ${push.data?.verified
              ? 'border-green-300 bg-green-50 text-green-900'
              : 'border-amber-300 bg-amber-50 text-amber-900'}`}
            >
              {push.data?.action === 'updated' ? 'Updated' : 'Registered'} as site
              {' '}#{push.data?.commio_location_id}
              {push.data?.verified
                ? ' — confirmed by reading it back at the carrier.'
                : ' — but reading it back failed, so this is not confirmed at the carrier.'}
            </p>
          )}
        </div>
      )}

      {picking && (
        <div className="mt-4 space-y-3">
          {sites.isLoading && <p className="text-sm text-gray-500">Reading the carrier’s sites…</p>}
          {sites.error && (
            <p className="text-sm text-red-700">
              Could not read the carrier’s sites: {sites.error.body?.error || sites.error.message}
            </p>
          )}

          {!sites.isLoading && !sites.error && (
            <>
              <label htmlFor="e911-search" className="block text-sm font-medium text-gray-700">
                {location.type ? `${location.type} sites` : 'Sites'} the carrier already has
              </label>
              {/* Searchable, like the other pickers. The carrier's site list runs to
                  hundreds and a bare <select> made finding one street a scroll (Kevin,
                  2026-08-10). */}
              <input
                id="e911-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type part of the address…"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <select
                id="e911-site"
                size={Math.min(8, Math.max(3, matches.length || 3))}
                value={choice}
                onChange={(e) => { setChoice(e.target.value); setTakeOverFrom(null); }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {matches.map((s) => (
                  <option key={s.commio_location_id} value={s.commio_location_id}>
                    {s.address || s.alias || `site ${s.commio_location_id}`}
                    {s.city ? `, ${s.city}` : ''}
                    {` · #${s.commio_location_id}`}
                    {s.verso_location ? ` — already on “${s.verso_location.label}”` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500">
                {query.trim()
                  ? `${matches.length} of ${usable.length} match`
                  : `${usable.length} site${usable.length === 1 ? '' : 's'}`}
              </p>

              {!usable.length && (
                <p className="text-sm text-amber-800">
                  The carrier has no {location.type} sites for this org — register this
                  address with them below.
                </p>
              )}
            </>
          )}

          {canEdit && (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <p className="text-sm text-gray-700">
                {linkedId
                  ? 'Address changed? Push it to the site this address is already linked to.'
                  : 'Not in the list? Register this address with the carrier.'}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  disabled={push.isPending || !location.addressLine1}
                  onClick={() => push.mutate()}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {push.isPending ? 'Registering…'
                    : location.commioLocationId ? 'Update the carrier’s site' : 'Register with the carrier'}
                </button>
                {!location.addressLine1 && (
                  <span className="text-xs text-amber-800">This address has no street line yet.</span>
                )}
              </div>
              {push.isError && (
                <p className="mt-2 text-sm text-red-700">
                  {push.error.body?.error || push.error.message}
                </p>
              )}
            </div>
          )}

          {takeOverFrom && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <p>
                That site is currently “{takeOverFrom.label || `location ${takeOverFrom.id}`}”.
                A site belongs to one address, so moving it here leaves that one unregistered
                for 911.
              </p>
              <button
                type="button"
                onClick={() => link.mutate({ takeOver: true })}
                disabled={link.isPending}
                className="mt-2 rounded-md bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800 disabled:opacity-50"
              >
                {link.isPending ? 'Moving…' : 'Move it here anyway'}
              </button>
            </div>
          )}

          {link.error && !takeOverFrom && (
            <p className="text-sm text-red-700">{link.error.body?.error || link.error.message}</p>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              disabled={!choice || link.isPending}
              onClick={() => link.mutate({})}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {link.isPending ? 'Linking…' : 'Link this site'}
            </button>
            <button
              type="button"
              onClick={() => { setPicking(false); setTakeOverFrom(null); link.reset(); }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            {linkedId && (
              <button
                type="button"
                onClick={() => link.mutate({ unlink: true })}
                disabled={link.isPending}
                title="Removes the 911 registration for this address. The carrier's own site is not deleted."
                className="ml-auto rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Unlink
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
